import React, { ReactNode, useCallback, useState } from 'react';
import { Alert, ConfigProvider, Flex, Form, Popover } from 'antd';
import { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrosshairs } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import {
	TimeZoneFormat,
	TimeZoneMode,
	ConsumptionSetAttachedTo,
	RequiredArrivalSpeedStatus,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { toISOTimezone } from '@shared/utils/timezones';
import { Values } from '@shared/utils/objectEnums';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import { Port } from '@api/utils/ports';
import DatePicker from '@client/components/DatePicker';
import Button from '@client/components/Button';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { calculateRequiredArrivalSpeed } from '@client/lib/api';
import useDisplayDateFromWallTime from '@client/utils/hooks/useDisplayDateFromWallTime';
import styles from './RequiredArrivalPopover.module.css';
import ArrivalInfoRow from './ArrivalInfoRow';

export const SINGLE_SPEED_FOR_FUEL_GRADE_ERROR = 'Only one speed is available for this fuel grade';

export type RequiredArrivalPreviewWarning =
	| {
		status: RequiredArrivalSpeedStatus.TOO_FAST;
		requiredSpeed: number;
		fastestAvailableSpeed: number;
	} |
	{
		status: RequiredArrivalSpeedStatus.TOO_SLOW;
		requiredSpeed: number;
		slowestAvailableSpeed: number;
	} |
	null;

type Props = {
	open: boolean;
	setOpen: (open: boolean) => void;
	disabled?: boolean;
	timeZoneFormat: TimeZoneFormat;
	currentArrivalDate: Moment | null;
	previousDepartureDate: Moment | null;
	fromPort: Port;
	toPort: Port;
	vesselId: number;
	options: SeaPassageSettings;
	onApply: (payload: {
		consumptionSetId: number;
	}) => void | Promise<void>;
	estimateId?: number | null;
	voyageId?: number;
	currentConsumptionSetId?: number | null;
	refreshAfterPreview?: () => void;
	successMessage?: string;
	triggerIcon?: ReactNode;
	attachedTo: Values<typeof ConsumptionSetAttachedTo>;
};

const formatSpeedFallbackMessage = ({
	type,
	limit,
	required,
}: {
	type: 'min' | 'max';
	limit: number;
	required: number;
}) => (
	`Used ${type} speed ${limit.toFixed(2)} kts ` +
	`(required ${required.toFixed(2)} kts).`
);

const buildDisabledTime = (
	previousDepartureDate?: Moment | null
) => (current?: Moment) => {
	if (!previousDepartureDate || !current) {
		return {};
	}

	const departure = toMoment(previousDepartureDate);

	if (!current.isSame(departure, 'day')) {
		return {};
	}

	return {
		disabledHours: () => {
			const hour = departure.hour();

			return Array.from({ length: hour }, (_, i) => i);
		},
		disabledMinutes: (hour: number) =>
			hour === departure.hour() ?
				Array.from({ length: departure.minute() }, (_, i) => i) :
				[],
	};
};

const getRequiredArrivalSpeedResult = async ({
	previousDepartureDate,
	requiredArrivalIso,
	fromPort,
	toPort,
	vesselId,
	estimateId,
	voyageId,
	currentConsumptionSetId,
	options,
	persistInterpolatedConsumptionSet,
	attachedTo,
}: {
	previousDepartureDate: Moment;
	requiredArrivalIso: string;
	fromPort: Port;
	toPort: Port;
	vesselId: number;
	estimateId?: number | null;
	voyageId?: number | null;
	currentConsumptionSetId?: number | null;
	options: SeaPassageSettings;
	persistInterpolatedConsumptionSet?: boolean;
	attachedTo: Values<typeof ConsumptionSetAttachedTo>
}) => {
	return calculateRequiredArrivalSpeed({
		departureDate: toMoment(previousDepartureDate),
		targetArrivalDate: toMoment(requiredArrivalIso),
		fromPort,
		toPort,
		vesselId,
		estimateId,
		voyageId,
		currentConsumptionSetId,
		options,
		persistInterpolatedConsumptionSet,
		attachedTo,
	});
};

const RequiredArrivalPopover = ({
	open,
	setOpen,
	disabled: disabledProp,
	timeZoneFormat,
	currentArrivalDate,
	previousDepartureDate,
	fromPort,
	toPort,
	vesselId,
	estimateId,
	voyageId,
	currentConsumptionSetId,
	options,
	onApply,
	refreshAfterPreview,
	successMessage = 'Required arrival set successfully',
	triggerIcon,
	attachedTo,
}: Props) => {
	const { componentDisabled } = ConfigProvider.useConfig();
	const disabled = disabledProp || componentDisabled;
	const [previewDate, setPreviewDate] = useState<Moment | null>(null);
	const [previewSpeed, setPreviewSpeed] = useState<number | null>(null);
	const [previewWarning, setPreviewWarning] = useState<RequiredArrivalPreviewWarning>(null);
	const [singleSpeedForFuelGrade, setSingleSpeedForFuelGrade] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const resetPreview = useCallback(() => {
		setPreviewDate(null);
		setPreviewSpeed(null);
		setPreviewWarning(null);
		setSingleSpeedForFuelGrade(false);
	}, []);

	const getRequiredArrivalIso = useCallback((picked: Moment) => {
		return toISOTimezone(
			picked,
			timeZoneFormat,
			toPort?.timezone,
			TimeZoneMode.WallTime,
		);
	}, [timeZoneFormat, toPort?.timezone]);

	const previewTargetDisplay = useDisplayDateFromWallTime(
		previewDate,
		timeZoneFormat,
		toPort?.timezone,
	);

	const currentArrivalDisplay = useDisplayDateFromWallTime(
		currentArrivalDate,
		timeZoneFormat,
		toPort?.timezone,
	);

	const previousDepartureDisplay = useDisplayDateFromWallTime(
		previousDepartureDate,
		timeZoneFormat,
		fromPort?.timezone,
	);

	const handlePreviewChange = useCallback(async (picked: Moment | null) => {
		if (picked == null || previousDepartureDate == null) {
			resetPreview();

			return;
		}

		setPreviewDate(picked);

		try {
			const requiredArrivalIso = getRequiredArrivalIso(picked);

			if (requiredArrivalIso == null) {
				resetPreview();

				return;
			}

			const result = await getRequiredArrivalSpeedResult({
				previousDepartureDate,
				requiredArrivalIso,
				fromPort,
				toPort,
				vesselId,
				estimateId,
				voyageId,
				currentConsumptionSetId,
				options,
				attachedTo,
			});

			if (result.status === RequiredArrivalSpeedStatus.SINGLE_SPEED_FOR_FUEL_GRADE) {
				setSingleSpeedForFuelGrade(true);
				setPreviewSpeed(result.availableSpeed);

				if (result.rangeStatus === RequiredArrivalSpeedStatus.TOO_FAST) {
					setPreviewWarning({
						status: RequiredArrivalSpeedStatus.TOO_FAST,
						requiredSpeed: result.requiredSpeed,
						fastestAvailableSpeed: result.availableSpeed,
					});
				} else if (result.rangeStatus === RequiredArrivalSpeedStatus.TOO_SLOW) {
					setPreviewWarning({
						status: RequiredArrivalSpeedStatus.TOO_SLOW,
						requiredSpeed: result.requiredSpeed,
						slowestAvailableSpeed: result.availableSpeed,
					});
				} else {
					setPreviewWarning(null);
				}

				return;
			}

			setSingleSpeedForFuelGrade(false);

			if (result.status === RequiredArrivalSpeedStatus.MATCHED) {
				setPreviewSpeed(result.appliedSpeed);
				setPreviewWarning(null);
				refreshAfterPreview?.();

				return;
			}

			if (result.status === RequiredArrivalSpeedStatus.TOO_FAST) {
				setPreviewSpeed(result.fastestAvailableSpeed);
				setPreviewWarning({
					status: RequiredArrivalSpeedStatus.TOO_FAST,
					requiredSpeed: result.requiredSpeed,
					fastestAvailableSpeed: result.fastestAvailableSpeed,
				});
			} else {
				setPreviewSpeed(result.slowestAvailableSpeed);
				setPreviewWarning({
					status: RequiredArrivalSpeedStatus.TOO_SLOW,
					requiredSpeed: result.requiredSpeed,
					slowestAvailableSpeed: result.slowestAvailableSpeed,
				});
			}
		} catch (e) {
			setPreviewSpeed(null);
			setPreviewWarning(null);
			setSingleSpeedForFuelGrade(false);
			showErrorNotification(
				e instanceof Error ? e.message : 'Could not set required arrival',
				e as Error,
			);
		}
	}, [
		estimateId,
		previousDepartureDate,
		resetPreview,
		getRequiredArrivalIso,
		fromPort,
		toPort,
		vesselId,
		voyageId,
		currentConsumptionSetId,
		options,
		attachedTo,
		refreshAfterPreview,
	]);

	const speedLabel = previewWarning != null ? 'Applied speed:' : 'Estimated speed:';

	return (
		<Popover
			trigger="click"
			open={!disabled && open}
			onOpenChange={(nextOpen) => {
				if (disabled) {
					return;
				}

				setOpen(nextOpen);

				if (nextOpen) {
					setPreviewDate(currentArrivalDate ? toMoment(currentArrivalDate) : null);
					setPreviewSpeed(null);
					setPreviewWarning(null);
					setSingleSpeedForFuelGrade(false);
				} else {
					resetPreview();
				}
			}}
			content={(
				<div onClick={(e) => e.stopPropagation()}>
					<Form
						initialValues={{
							requiredArrival: currentArrivalDate ?
								toMoment(currentArrivalDate) :
								null,
						}}
						layout="vertical"
						onFinish={async (values) => {
							const picked: Moment | null = values.requiredArrival ?? null;

							if (picked == null || previousDepartureDate == null) {
								return;
							}

							if (singleSpeedForFuelGrade) {
								return;
							}

							setSubmitting(true);

							try {
								const requiredArrivalIso = getRequiredArrivalIso(picked);

								if (requiredArrivalIso == null) {
									resetPreview();

									return;
								}

								const result = await getRequiredArrivalSpeedResult({
									previousDepartureDate,
									requiredArrivalIso,
									fromPort,
									toPort,
									vesselId,
									estimateId,
									voyageId,
									currentConsumptionSetId,
									options,
									persistInterpolatedConsumptionSet: true,
									attachedTo,
								});

								if (result.status === RequiredArrivalSpeedStatus.SINGLE_SPEED_FOR_FUEL_GRADE) {
									setSingleSpeedForFuelGrade(true);
									setPreviewSpeed(result.availableSpeed);

									if (result.rangeStatus === RequiredArrivalSpeedStatus.TOO_FAST) {
										setPreviewWarning({
											status: RequiredArrivalSpeedStatus.TOO_FAST,
											requiredSpeed: result.requiredSpeed,
											fastestAvailableSpeed: result.availableSpeed,
										});
									} else if (result.rangeStatus === RequiredArrivalSpeedStatus.TOO_SLOW) {
										setPreviewWarning({
											status: RequiredArrivalSpeedStatus.TOO_SLOW,
											requiredSpeed: result.requiredSpeed,
											slowestAvailableSpeed: result.availableSpeed,
										});
									}

									return;
								}

								if (result.consumptionSetId == null) {
									return;
								}

								await onApply({
									consumptionSetId: result.consumptionSetId,
								});

								if (result.status === RequiredArrivalSpeedStatus.TOO_FAST) {
									showSuccessNotification(
										formatSpeedFallbackMessage({
											type: 'max',
											limit: result.fastestAvailableSpeed,
											required: result.requiredSpeed,
										}),
									);
								} else if (result.status === RequiredArrivalSpeedStatus.TOO_SLOW) {
									showSuccessNotification(
										formatSpeedFallbackMessage({
											type: 'min',
											limit: result.slowestAvailableSpeed,
											required: result.requiredSpeed,
										}),
									);
								} else {
									showSuccessNotification(successMessage);
								}

								setOpen(false);
								resetPreview();
							} catch (e) {
								showErrorNotification(
									e instanceof Error ? e.message : 'Could not set required arrival',
									e as Error,
								);
							} finally {
								setSubmitting(false);
							}
						}}
					>
						<Form.Item
							name="requiredArrival"
							label={<span>Required Arrival</span>}
							rules={[{ required: true, message: 'Please choose a required arrival time' }]}
						>
							<DatePicker
								time
								showTimezone={false}
								disableTimezone
								disabledDate={(current) => {
									if (!previousDepartureDate) {
										return false;
									}

									const departure = toMoment(previousDepartureDate);

									return current.isBefore(departure, 'day');
								}}
								disabledTime={buildDisabledTime(previousDepartureDate)}

								onChange={async (picked) => {
									if (!picked || Array.isArray(picked)) {
										resetPreview();

										return;
									}

									await handlePreviewChange(picked as Moment);
								}}
							/>
						</Form.Item>
						<Alert
							className={styles.alert}
							type="info"
							showIcon
							message={`This will adjust speed for ${fromPort?.name} → ${toPort?.name}`}
							description={(
								<Flex vertical gap={4}>
									<ArrivalInfoRow
										label="Current arrival:"
										value={currentArrivalDisplay}
										timeZoneFormat={timeZoneFormat}
									/>

									<ArrivalInfoRow
										label="Target arrival:"
										value={previewTargetDisplay}
										timeZoneFormat={timeZoneFormat}
										fallback="Not selected"
									/>

									<ArrivalInfoRow
										label="Previous departure:"
										value={previousDepartureDisplay}
										timeZoneFormat={timeZoneFormat}
									/>

									<Flex gap={4}>
										<strong>{speedLabel}</strong>
										<span>{previewSpeed != null ? `${previewSpeed.toFixed(2)} kts` : '—'}</span>
									</Flex>
								</Flex>
							)}
						/>
						{singleSpeedForFuelGrade && (
							<Alert
								className={styles.alert}
								type="error"
								showIcon
								message={SINGLE_SPEED_FOR_FUEL_GRADE_ERROR}
							/>
						)}
						{previewWarning != null && (
							<Alert
								className={styles.alert}
								type="warning"
								showIcon
								message={
									previewWarning.status === RequiredArrivalSpeedStatus.TOO_FAST ?
										'Required speed exceeds available range' :
										'Required speed is below available range'
								}
								description={
									<Flex vertical gap={4}>
										{previewWarning.status === RequiredArrivalSpeedStatus.TOO_FAST ? (
											<>
												<div>
													To arrive at the selected time, the vessel would need about{' '}
													<strong>{previewWarning.requiredSpeed.toFixed(2)} kts</strong>.
												</div>
												<div>
													The fastest available speed pair is{' '}
													<strong>{previewWarning.fastestAvailableSpeed.toFixed(2)} kts</strong>,
													so that speed will be applied instead.
												</div>
											</>
										) : (
											<>
												<div>
													To arrive at the selected time, the vessel would need about{' '}
													<strong>{previewWarning.requiredSpeed.toFixed(2)} kts</strong>.
												</div>
												<div>
													The slowest available speed pair is{' '}
													<strong>{previewWarning.slowestAvailableSpeed.toFixed(2)} kts</strong>,
													so that speed will be applied instead.
												</div>
											</>
										)}
									</Flex>
								}
							/>
						)}
						<Form.Item style={{ marginBottom: 0 }}>
							<Button
								htmlType="submit"
								type="primary"
								disabled={disabled || singleSpeedForFuelGrade}
								loading={submitting}
							>
								{submitting ? 'Calculating...' : 'Set Required Arrival'}
							</Button>
						</Form.Item>
					</Form>
				</div>
			)}
		>
			<span onClick={(e) => e.stopPropagation()}>
				{triggerIcon ?? (
					<FontAwesomeIcon
						className={classNames(styles.cursor, { [styles.disabled]: disabled })}
						color="#1677ff"
						icon={faCrosshairs}
					/>
				)}
			</span>
		</Popover>
	);
};

export default RequiredArrivalPopover;
